'use client';

import { useIntl } from 'react-intl';
import { useConfig } from '@packages/shared/src/hooks/useConfig/useConfig';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@packages/shared/src/ui/Tabs/Tabs';

import { useInvoiceState } from './useInvoiceState';
import type { CustomerInvoicesProps } from './types';
import { TabContent as TabContentComponent } from './TabContent.client';
import { messages } from './messages';
import { isInvoiceStatusFilter, DEFAULT_STATUS } from './constants';

/* GraphQL */ `
  query CustomerInvoices(
    $maxAgeDays: Int
    $paginationKey: String
    $status: CustomerInvoiceStatus
    $locale: String
  ) {
    customerInvoices(
      maxAgeDays: $maxAgeDays
      paginationKey: $paginationKey
      status: $status
      locale: $locale
    ) {
      invoices {
        status
        invoiceId
        invoiceDate
        paymentHint
        invoiceNumber
        products {
          name
          imageUrl
          url
        }
        dueDateFormatted
        paylink
      }
      nextPaginationKey
    }
  }
`;

export const CustomerInvoices = ({
  enableTabsMode: overrideEnableTabsMode,
}: CustomerInvoicesProps = {}) => {
  const intl = useIntl();
  const {
    order: {
      customerInvoices: { enableTabsMode },
    },
  } = useConfig();

  const effectiveEnableTabsMode = overrideEnableTabsMode ?? enableTabsMode ?? true;

  const state = useInvoiceState(effectiveEnableTabsMode);

  const handleTabChange = (newTab: string) => {
    const statusFilter = isInvoiceStatusFilter(newTab) ? newTab : DEFAULT_STATUS;
    state.setActiveTab(statusFilter);
  };

  if (!effectiveEnableTabsMode) {
    return (
      <div className="bg-color-grey-light p-2">
        <TabContentComponent tab={state.activeTab} state={state} />
      </div>
    );
  }

  return (
    <Tabs value={state.activeTab} onValueChange={handleTabChange}>
      <TabsList className="grid w-full grid-cols-2 md:w-[50%]">
        <TabsTrigger
          className={
            state.activeTab === 'Open' ? 'text-color-text-dark font-bold' : 'text-color-text-dark'
          }
          value="Open"
        >
          {intl.formatMessage(messages.tabOpen)}
        </TabsTrigger>
        <TabsTrigger
          className={
            state.activeTab === 'All' ? 'text-color-text-dark font-bold' : 'text-color-text-dark'
          }
          value="All"
        >
          {intl.formatMessage(messages.tabAll)}
        </TabsTrigger>
      </TabsList>
      <TabsContent value="Open" className="bg-color-grey-light mt-0 p-2 md:p-3">
        <TabContentComponent tab="Open" state={state} />
      </TabsContent>
      <TabsContent value="All" className="bg-color-grey-light mt-0 p-2 md:p-3">
        <TabContentComponent tab="All" state={state} />
      </TabsContent>
    </Tabs>
  );
};
